/**
Copyright 2021 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License.
*/
import React, { useState, useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import { useSkills } from "../../hooks/community/useSkills";
import { useUserSkillInterest } from "../../hooks/community/useUserSkillInterest";
import { GetTranslation } from "../../utils/translationService";
import styles from "./ALMSkillComponent.module.css";

const ALMSkillComponent = (props: any) => {
    const skillCmp = useRef<HTMLDivElement | null>(null);
    const [dataProps, setDataProps] = useState([] as any);
  const { formatMessage } = useIntl();
  const {
    items,
    fetchUserSkillInterest,
    saveUserSkillInterest,
    removeUserSkillInterest,
    loadMoreUserSkillInterest,
    hasMoreItems,
  } = useUserSkillInterest();
  const { skills, fetchSkills, hasMoreSkills, loadMoreSkills } = useSkills();
  const [mode, setMode] = useState("view");
  const [selectedInterest, setSelectedInterest] = useState([] as any);
  const generalSkill = "general";

  const removeFocus_lxpv = (event: Event) => {
    const currentSkill = event.target as HTMLElement;
    if (currentSkill) {
      currentSkill.blur();
    }
  };

  const clearSelectedSkills = () => {
    setSelectedInterest([]);
  };

  const loadMoreSkillsHandler = async () => {
    await loadMoreSkills();
  };

  const toggleSkillSelection = (node: any) => {
    const skillId = node.id.split("skill-")[1];
    let selectedSkills = selectedInterest;
    if (selectedSkills.indexOf(skillId) > -1) {
      selectedSkills.splice(selectedSkills.indexOf(skillId), 1);
      node.style.boxShadow = "none";
      node.style.color = "";
      node.style.fontWeight = "";
      node.style.border = "";
      node.setAttribute("aria-pressed", "false");
    } else {
      selectedSkills.push(skillId);
      node.style.border = "1px solid var(--alm-skill-primary-color, #009CDE)";
      node.style.color = "#002A3A";
      node.style.fontWeight = "700";
      node.setAttribute("aria-pressed", "true");
    }
    setSelectedInterest(selectedSkills);
  };

  const handleClickOnSkillBox = (event: any) => {
    let node = event.target as HTMLElement;
    if (
      node.className.indexOf("skillBoxSelectable") > -1 ||
      node.className.indexOf("skillNameSelectable") > -1
    ) {
      if (node.className.indexOf("skillName") > -1) {
        node = node.parentNode as HTMLElement;
      }
      toggleSkillSelection(node);
    }
  };

  const scrollToSkillsSection = () => {
    document.getElementById("skills-section")?.scrollIntoView();
  };

  const editSkillInterest = async () => {
    await fetchSkills();
    setMode("edit");
    scrollToSkillsSection();
  };

  const saveSkillInterest = async () => {
    if (selectedInterest.length !== 0) {
      await saveUserSkillInterest(selectedInterest);
      await fetchUserSkillInterest();
    }
    discardSelection();
  };

  const discardSelection = () => {
    clearSelectedSkills();
    setMode("view");
    scrollToSkillsSection();
  };

  const noSkillInterestPresent = () => {
    return (
      !items ||
      !items.length ||
      items.length === 0 ||
      (items.length === 1 && items[0].skill.name.toLowerCase() === generalSkill)
    );
  };

  const noSkillPresent = () => {
    return (
      !skills ||
      !skills.length ||
      skills.length === 0 ||
      (skills.length === 1 && skills[0].name.toLowerCase() === generalSkill)
    );
  };

  const removeSkillInterest = async (skillId: any) => {
    await removeUserSkillInterest(skillId);
  };

  const getSkillLevelName = (levelId: any) => {
    if (levelId === "1")
      return formatMessage({
        id: "alm.profile.skillLevel.beginner",
        defaultMessage: "Beginner",
      });
    else if (levelId === "2")
      return formatMessage({
        id: "alm.profile.skillLevel.intermediate",
        defaultMessage: "Intermediate",
      });
    else if (levelId === "3")
      return formatMessage({
        id: "alm.profile.skillLevel.advanced",
        defaultMessage: "Advanced",
      });
    else return "";
  };

  const getFormattedSourceString = (source: any) => {
    if (source === "ADMIN_ASSIGN")
      return formatMessage({
        id: "alm.profile.skills.adminAssign",
        defaultMessage: "Added by Admin",
      });
    else if (source === "LO_ENROLL")
      return formatMessage({
        id: "alm.profile.skills.loEnroll",
        defaultMessage: "Added based on your learnings",
      });
    else if (source === "USER_SELECTED")
      return formatMessage({
        id: "alm.profile.skills.selfAssigned",
        defaultMessage: "Self Assigned",
      });
    else return "";
  };
  const getCreditsPercent = (a: any, b: any) => {
    const percent = (a * 100) / b > 100 ? 100 : (a * 100) / b;
    return " " + Math.round((percent + Number.EPSILON) * 100) / 100;
  };

  const populateUserSkillInterestLevelData = (userSkillInterest: any) => {
    let pointsObject = {};
    let pointsArray = [];
    if (userSkillInterest.userSkills) {
      for (let i = 0; i < userSkillInterest.userSkills.length; i++) {
        const skillEnrollmentDetails =
          userSkillInterest.userSkills[i].id.split("_");
        const levelId = skillEnrollmentDetails[2];

        if (levelId) {
          pointsObject = {
            levelId: levelId,
            pointsAchieved: userSkillInterest.userSkills[i].pointsEarned,
            totalPoints: userSkillInterest.userSkills[i].skillLevel.maxCredits,
          };
          pointsArray.push(pointsObject);
        }
      }
    }
    return pointsArray;
  };

  useEffect(() => {
      if (skillCmp.current) {
          const parentEl = skillCmp.current.parentElement;

          if (parentEl?.dataset) {
              const attr = parentEl?.getAttribute("data-props");
              if (attr) {

                  try {
                        const dataValueProps = JSON.parse(attr);
                        setDataProps(dataValueProps);
                  } catch (error) {
                      console.error("error skills", error)
                  }
              }

          }



      }
  }, []);

  return (
    <>
      <section id="skills-section" className={styles.skillsArea} ref={skillCmp}>
        <div className={styles.skillsHeading}>
          {dataProps.cmpTitle || formatMessage({
            id: "alm.profile.skills.areasOfInterest",
            defaultMessage: "Your Areas of Interest",
          })}
        </div>
        <hr />
        <div className={styles.skillsDescription}>
          {dataProps.cmpDescription || formatMessage({
            id: "alm.profile.skills.description",
            defaultMessage:
              "Select areas of interest. You will see recommendations based on your interest.",
          })}
        </div>
        {/* {!items &&
                    <ALMLoader></ALMLoader>
                } */}
        {mode === "view" && items?.length > 0 && (
          <div className={styles.skillsContainer}>
            {items
              .filter(
                (skillInterest) =>
                  skillInterest.skill.name.toLowerCase() !== generalSkill
              )
              .sort((a, b) => a.skill.name.localeCompare(b.skill.name)).map((skillInterest) => {
                const id = skillInterest.skill.id;

                return (
                  <div
                    key={id}
                    className={styles.skillBox}
                    tabIndex={0}
                    id={"skill-" + id}
                    aria-labelledby={"skillName-" + id}
                    role="group"
                    onMouseDown={(event: any) => {
                      handleClickOnSkillBox(event);
                    }}
                    onMouseLeave={(event: any) => {
                      removeFocus_lxpv(event);
                    }}
                  >
                    <span className={styles.skillName}>
                      {skillInterest.skill.name}
                    </span>
                    <button
                      onClick={() => {
                        removeSkillInterest(id);
                      }}
                      className={styles.removeInterestButton}
                      aria-label={formatMessage({
                        id: "alm.profile.skills.removeInterest",
                        defaultMessage: "Remove from My interests",
                      })}
                      title={formatMessage({
                        id: "alm.profile.skills.removeInterest",
                        defaultMessage: "Remove from My interests",
                      })}
                    ></button>
                    <div className={styles.arrowUp}></div>
                    <div className={styles.skillDataTooltip} aria-hidden="true">
                      {skillInterest.skill.name}
                      <br />
                      {skillInterest.source === "USER_SELECTED" ? (
                        ``
                      ) : (
                        <div>
                          {getFormattedSourceString(skillInterest.source)}
                        </div>
                      )}
                      {populateUserSkillInterestLevelData(skillInterest).map(
                        (level: any) => {
                          return (
                            <div
                              key={`${level.levelId}_${skillInterest.skill.id}`}
                            >
                              <strong>
                                {getSkillLevelName(level.levelId)}
                              </strong>
                              :
                              {getCreditsPercent(
                                level.pointsAchieved,
                                level.totalPoints
                              )}
                              %
                              {formatMessage({
                                id: "alm.profile.skills.achieved",
                                defaultMessage: " Achieved",
                              })}
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        )}
        {mode === "view" && hasMoreItems && (
          <button
            className={styles.showMoreButton}
            onClick={loadMoreUserSkillInterest}
          >
            {dataProps.viewMoreLabel || formatMessage({
              id: "alm.profile.skills.viewMore",
              defaultMessage: "View more",
            })}
          </button>
        )}
        {mode === "view" && noSkillInterestPresent() && (
          <div className={styles.skillsContainer}>
            <span>
              {GetTranslation("alm.profile.skills.noSkillInterest", true)}
            </span>
          </div>
        )}
        {mode === "view" && (
          <div className={styles.modifyInterest}>
            <button
              className={`almButton primary ${styles.actionButton}`}
              onClick={editSkillInterest}
            >
              {dataProps.modifyButtonText || formatMessage({
                id: "alm.profile.skills.modifyInterest",
                defaultMessage: "Modify Interest",
              })}
            </button>
          </div>
        )}
        {mode === "edit" && skills?.length > 0 && (
          <div className={styles.skillsContainer}>
            {skills
              .filter((skill) => skill.name.toLowerCase() !== generalSkill)
              .map((skill: any) => (
                <button
                  className={styles.skillBoxSelectable}
                  tabIndex={0}
                  id={"skill-" + skill.id}
                  aria-labelledby={"skillName-" + skill.id}
                  onClick={(event: any) => {
                    handleClickOnSkillBox(event);
                  }}
                  onMouseLeave={(event: any) => {
                    removeFocus_lxpv(event);
                  }}
                  aria-pressed={false}
                >
                  <span className={styles.skillNameSelectable}>
                    {skill.name}
                  </span>
                </button>
              ))}
          </div>
        )}
        {mode === "edit" && hasMoreSkills && (
          <button
            className={styles.showMoreButton}
            onClick={loadMoreSkillsHandler}
          >
            {formatMessage({
              id: "alm.profile.skills.viewMore",
              defaultMessage: "View more",
            })}
          </button>
        )}
        {mode === "edit" && noSkillPresent() && (
          <div className={styles.skillsContainer}>
            <span>
              {GetTranslation("alm.profile.skills.noSkill", true)}
            </span>
          </div>
        )}
        {mode === "edit" && (
          <div className={styles.modifyInterest}>
            <button
              className={`almButton primary ${styles.actionButton}`}
              onClick={saveSkillInterest}
            >
              {dataProps.addButtonText || formatMessage({
                id: "alm.profile.skills.addInterest",
                defaultMessage: "Add Interest",
              })}
            </button>
            <button
              className={`almButton secondary ${styles.actionButton}`}
              onClick={discardSelection}
            >
              {dataProps.cancelButtonText || formatMessage({
                id: "alm.profile.skills.cancel",
                defaultMessage: "Cancel",
              })}
            </button>
          </div>
        )}
      </section>
    </>
  );
};

export default ALMSkillComponent;
